export type BadgeRarity = 'common' | 'rare' | 'epic' | 'legendary';
export type BadgeCategory = 'explorer' | 'learner' | 'competitor' | 'master' | 'social';

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  rarity: BadgeRarity;
  category: BadgeCategory;
  xpReward: number;
  criteria: {
    type: 'visit' | 'quiz' | 'game' | 'exploration' | 'completion' | 'ai';
    target?: number;
    threshold?: number;
  };
}

export const ACHIEVEMENTS: Badge[] = [
  {
    id: 'first-steps',
    name: 'First Steps',
    description: 'Complete the onboarding tutorial',
    icon: '\u{1F463}',
    rarity: 'common',
    category: 'explorer',
    xpReward: 10,
    criteria: { type: 'completion', target: 1 },
  },
  {
    id: 'court-visitor',
    name: 'Court Visitor',
    description: 'Visit the interactive court diagram',
    icon: '\u{1F3DF}',
    rarity: 'common',
    category: 'explorer',
    xpReward: 10,
    criteria: { type: 'visit', target: 1 },
  },
  {
    id: 'rule-reader',
    name: 'Rule Reader',
    description: 'Read all rule sections',
    icon: '\u{1F4D6}',
    rarity: 'common',
    category: 'explorer',
    xpReward: 20,
    criteria: { type: 'visit', target: 1 },
  },
  {
    id: 'game-starter',
    name: 'Game Starter',
    description: 'Play your first mini-game',
    icon: '\u{1F3AE}',
    rarity: 'common',
    category: 'explorer',
    xpReward: 15,
    criteria: { type: 'game', target: 1 },
  },
  {
    id: 'ai-curious',
    name: 'AI Curious',
    description: 'Ask the AI Referee your first question',
    icon: '\u{1F916}',
    rarity: 'common',
    category: 'explorer',
    xpReward: 15,
    criteria: { type: 'ai', target: 1 },
  },

  {
    id: 'court-explorer',
    name: 'Court Explorer',
    description: 'Click and explore all court zones',
    icon: '\u{1F9ED}',
    rarity: 'rare',
    category: 'learner',
    xpReward: 50,
    criteria: { type: 'exploration', threshold: 100 },
  },
  {
    id: 'quiz-novice',
    name: 'Quiz Novice',
    description: 'Complete your first quiz',
    icon: '\u{1F4DD}',
    rarity: 'rare',
    category: 'learner',
    xpReward: 30,
    criteria: { type: 'quiz', target: 1 },
  },
  {
    id: 'perfect-scorer',
    name: 'Perfect Scorer',
    description: 'Get 100% on any quiz',
    icon: '\u{1F3AF}',
    rarity: 'rare',
    category: 'learner',
    xpReward: 50,
    criteria: { type: 'quiz', threshold: 100 },
  },
  {
    id: 'dedicated-learner',
    name: 'Dedicated Learner',
    description: 'Reach 30% overall completion',
    icon: '\u{1F4DA}',
    rarity: 'rare',
    category: 'learner',
    xpReward: 40,
    criteria: { type: 'completion', threshold: 30 },
  },
  {
    id: 'ai-enthusiast',
    name: 'AI Enthusiast',
    description: 'Ask the AI Referee 10 questions',
    icon: '\u{1F4AC}',
    rarity: 'rare',
    category: 'learner',
    xpReward: 50,
    criteria: { type: 'ai', target: 10 },
  },

  {
    id: 'game-champion',
    name: 'Game Champion',
    description: 'Complete all mini-games',
    icon: '\u{1F3C6}',
    rarity: 'epic',
    category: 'competitor',
    xpReward: 100,
    criteria: { type: 'game', target: 4 },
  },
  {
    id: 'quiz-master',
    name: 'Quiz Master',
    description: 'Complete all quizzes with 100% score',
    icon: '\u{1F947}',
    rarity: 'epic',
    category: 'competitor',
    xpReward: 150,
    criteria: { type: 'quiz', threshold: 100, target: 5 },
  },
  {
    id: 'week-warrior',
    name: 'Week Warrior',
    description: 'Reach 60% overall completion',
    icon: '\u26A1',
    rarity: 'epic',
    category: 'competitor',
    xpReward: 100,
    criteria: { type: 'completion', threshold: 60 },
  },
  {
    id: 'speed-learner',
    name: 'Speed Learner',
    description: 'Reach 80% completion across all sections',
    icon: '\u{1F680}',
    rarity: 'epic',
    category: 'competitor',
    xpReward: 120,
    criteria: { type: 'completion', threshold: 80 },
  },
  {
    id: 'knowledge-seeker',
    name: 'Knowledge Seeker',
    description: 'Ask 25+ questions to the AI Referee',
    icon: '\u{1F50E}',
    rarity: 'epic',
    category: 'competitor',
    xpReward: 100,
    criteria: { type: 'ai', target: 25 },
  },

  {
    id: 'perfectionist',
    name: 'The Perfectionist',
    description: 'Achieve 100% completion across all sections',
    icon: '\u{1F48E}',
    rarity: 'legendary',
    category: 'master',
    xpReward: 250,
    criteria: { type: 'completion', threshold: 100 },
  },
  {
    id: 'completion-legend',
    name: 'Completion Legend',
    description: 'Master every lesson available',
    icon: '\u{1F451}',
    rarity: 'legendary',
    category: 'master',
    xpReward: 300,
    criteria: { type: 'completion', threshold: 100 },
  },
  {
    id: 'padel-master',
    name: 'Padel Master',
    description: 'Unlock all other achievements',
    icon: '\u{1F3BE}',
    rarity: 'legendary',
    category: 'master',
    xpReward: 500,
    criteria: { type: 'completion', target: 18 },
  },
];

export const RARITY_CONFIG: Record<BadgeRarity, {
  gradient: string;
  glow: string;
  border: string;
  text: string;
}> = {
  common: {
    gradient: 'from-slate-400 to-slate-500',
    glow: 'shadow-slate-500/20',
    border: 'border-slate-400',
    text: 'text-slate-600',
  },
  rare: {
    gradient: 'from-blue-400 to-blue-600',
    glow: 'shadow-blue-500/30',
    border: 'border-blue-400',
    text: 'text-blue-600',
  },
  epic: {
    gradient: 'from-purple-400 to-pink-600',
    glow: 'shadow-purple-500/40',
    border: 'border-purple-400',
    text: 'text-purple-600',
  },
  legendary: {
    gradient: 'from-yellow-400 via-orange-500 to-red-600',
    glow: 'shadow-yellow-500/50',
    border: 'border-yellow-400',
    text: 'text-yellow-600',
  },
};
